import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building,
  CreditCard,
  Trash2,
  Loader2,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  Crown,
  Check,
  Undo2,
  ArrowLeft,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useWorkspaceStore } from '@/store/workspace';
import { useSubscription } from '@/store/subscription';

type Section = 'workspace' | 'subscription' | 'danger';

export function WorkspaceSettings() {
  const navigate = useNavigate();
  const {
    currentWorkspace,
    updateWorkspace,
    deleteWorkspace,
    canEdit,
  } = useWorkspaceStore();
  const isAdmin = canEdit();

  const {
    subscription,
    isLoading: subLoading,
    isTrialing,
    isPro,
    isFree,
    trialDaysRemaining,
    createPortalSession,
    createCheckoutSession,
  } = useSubscription();

  const [activeSection, setActiveSection] = useState<Section>('workspace');

  // Workspace name state
  const [workspaceName, setWorkspaceName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  // Billing actions
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Delete state
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Section refs for scrolling
  const workspaceRef = useRef<HTMLDivElement>(null);
  const subscriptionRef = useRef<HTMLDivElement>(null);
  const dangerRef = useRef<HTMLDivElement>(null);

  // Sync workspace name when workspace changes
  useEffect(() => {
    setWorkspaceName(currentWorkspace?.name || '');
  }, [currentWorkspace?.id, currentWorkspace?.name]);

  // Non-admins shouldn't be on this page; bounce to dashboard.
  useEffect(() => {
    if (currentWorkspace && !isAdmin) {
      navigate('/home', { replace: true });
    }
  }, [currentWorkspace, isAdmin, navigate]);

  const scrollToSection = (section: Section) => {
    setActiveSection(section);
    const ref =
      section === 'workspace'
        ? workspaceRef
        : section === 'subscription'
        ? subscriptionRef
        : dangerRef;
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSaveName = async () => {
    if (!currentWorkspace) return;
    setIsSavingName(true);
    setNameError(null);
    try {
      await updateWorkspace(currentWorkspace.id, { name: workspaceName });
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Failed to update name');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const url = await createPortalSession();
      if (url) window.location.href = url;
    } finally {
      setPortalLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      const priceId = import.meta.env.VITE_STRIPE_PRO_PRICE_ID;
      const url = await createCheckoutSession(priceId);
      if (url) window.location.href = url;
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!currentWorkspace) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteWorkspace(currentWorkspace.id);
      navigate('/workspace/select');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete workspace');
      setDeleteLoading(false);
    }
  };

  // Subscription status display
  const getStatusInfo = () => {
    if (!subscription) {
      return { icon: XCircle, label: 'Free', color: 'text-gray-500', bgColor: 'bg-gray-100' };
    }
    switch (subscription.status) {
      case 'trialing':
        return {
          icon: Clock,
          label: 'Trial',
          color: trialDaysRemaining <= 3 ? 'text-amber-600' : 'text-blue-600',
          bgColor: trialDaysRemaining <= 3 ? 'bg-amber-100' : 'bg-blue-100',
        };
      case 'active':
        return { icon: CheckCircle, label: 'Active', color: 'text-green-600', bgColor: 'bg-green-100' };
      case 'past_due':
        return { icon: AlertTriangle, label: 'Past Due', color: 'text-red-600', bgColor: 'bg-red-100' };
      case 'canceled':
      case 'free':
        return { icon: XCircle, label: 'Free', color: 'text-gray-600', bgColor: 'bg-gray-100' };
      default:
        return { icon: XCircle, label: subscription.status, color: 'text-gray-600', bgColor: 'bg-gray-100' };
    }
  };

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;

  const sections: { id: Section; label: string; icon: typeof Building }[] = [
    { id: 'workspace', label: 'Workspace', icon: Building },
    { id: 'subscription', label: 'Subscription', icon: CreditCard },
    { id: 'danger', label: 'Danger Zone', icon: Trash2 },
  ];

  if (!currentWorkspace) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-8 px-4">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Workspace Settings</h1>
        </div>

        {/* Hot Links Navigation */}
        <div className="flex gap-2 mb-8 pb-4 border-b overflow-x-auto">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? section.id === 'danger'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-ingesta-blue-100 text-ingesta-blue-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {section.label}
              </button>
            );
          })}
        </div>

        {/* Workspace section */}
        <div ref={workspaceRef} className="scroll-mt-4">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Workspace
              </CardTitle>
              <CardDescription>
                The name and identifier for this workspace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="workspace-name" className="text-sm font-medium text-gray-700">
                  Name
                </label>
                <div className="flex gap-2">
                  <Input
                    id="workspace-name"
                    value={workspaceName}
                    onChange={(e) => setWorkspaceName(e.target.value)}
                    placeholder="Workspace name"
                    disabled={isSavingName}
                  />
                  {workspaceName !== currentWorkspace.name && workspaceName.trim() && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setWorkspaceName(currentWorkspace.name)}
                        title="Revert"
                        disabled={isSavingName}
                      >
                        <Undo2 className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        onClick={handleSaveName}
                        disabled={isSavingName}
                        title="Apply"
                      >
                        {isSavingName ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4" />
                        )}
                      </Button>
                    </>
                  )}
                </div>
                {nameError && <p className="text-sm text-red-600">{nameError}</p>}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription section */}
        <div ref={subscriptionRef} className="scroll-mt-4">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Subscription
              </CardTitle>
              <CardDescription>
                Billing and plan management for this workspace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {subLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  {/* Current Plan */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${statusInfo.bgColor}`}>
                        <StatusIcon className={`h-5 w-5 ${statusInfo.color}`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-gray-900">
                            {isPro || isTrialing ? 'Pro Plan' : 'Free Plan'}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${statusInfo.bgColor} ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">
                          {isTrialing && (
                            <>
                              {trialDaysRemaining === 1
                                ? '1 day remaining in trial'
                                : `${trialDaysRemaining} days remaining in trial`}
                            </>
                          )}
                          {subscription?.status === 'active' && <>$2.99/month</>}
                          {isFree && !isTrialing && <>Limited to 3 projects, no AI features</>}
                          {subscription?.status === 'past_due' && (
                            <span className="text-red-600">Payment failed - please update your payment method</span>
                          )}
                        </p>
                      </div>
                    </div>
                    {(isPro || isTrialing) && (
                      <Crown className="h-6 w-6 text-amber-500" />
                    )}
                  </div>

                  {/* Plan Details */}
                  {subscription && (
                    <div className="space-y-3 text-sm">
                      {subscription.current_period_end && subscription.status === 'active' && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Next billing date</span>
                          <span className="text-gray-900">
                            {new Date(subscription.current_period_end).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {subscription.trial_end_date && isTrialing && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Trial ends</span>
                          <span className="text-gray-900">
                            {new Date(subscription.trial_end_date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                      {subscription.cancel_at_period_end && (
                        <div className="flex justify-between">
                          <span className="text-gray-500">Cancels on</span>
                          <span className="text-red-600">
                            {subscription.current_period_end
                              ? new Date(subscription.current_period_end).toLocaleDateString()
                              : 'End of period'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Pro Features */}
                  <div className="pt-4 border-t">
                    <h4 className="flex items-center gap-2 font-medium mb-3">
                      <Crown className="h-4 w-4 text-amber-500" />
                      Pro Features
                    </h4>
                    <ul className="space-y-2">
                      {[
                        'Unlimited projects',
                        'AI-powered project insights',
                        'Smart status generation',
                        'Priority support',
                      ].map(feature => (
                        <li key={feature} className="flex items-center gap-2 text-sm">
                          <CheckCircle className={`h-4 w-4 ${isPro || isTrialing ? 'text-green-500' : 'text-gray-300'}`} />
                          <span className={isPro || isTrialing ? 'text-gray-900' : 'text-gray-500'}>
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Actions */}
                  <div className="pt-4 border-t space-y-3">
                    {subscription?.stripe_customer_id ? (
                      <Button
                        onClick={handleManageSubscription}
                        disabled={portalLoading}
                        className="w-full"
                      >
                        {portalLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Manage Subscription
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        onClick={handleUpgrade}
                        disabled={checkoutLoading}
                        className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                      >
                        {checkoutLoading ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <Crown className="h-4 w-4 mr-2" />
                            Upgrade to Pro
                          </>
                        )}
                      </Button>
                    )}
                    <p className="text-xs text-gray-500 text-center">
                      {subscription?.stripe_customer_id
                        ? 'Manage your payment method, view invoices, or cancel your subscription'
                        : 'Start your 7-day free trial with full Pro features'}
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Danger Zone */}
        <div ref={dangerRef} className="scroll-mt-4">
          <Card className="mb-6 border-red-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <Trash2 className="h-5 w-5" />
                Danger Zone
              </CardTitle>
              <CardDescription>
                Irreversible workspace operations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="border border-red-200 bg-red-50/40 rounded-lg p-4 space-y-3">
                <h4 className="text-sm font-medium text-red-900">Delete this workspace</h4>
                <p className="text-sm text-gray-700">
                  Permanently delete <strong>{currentWorkspace.name}</strong> along with all
                  projects, documents, and members. The Stripe subscription is canceled
                  immediately. This cannot be undone.
                </p>
                <div>
                  <p className="text-sm text-gray-700 mb-1">
                    Type <span className="font-mono font-semibold bg-red-100 px-1 rounded">delete {currentWorkspace.name}</span> to confirm:
                  </p>
                  <Input
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    placeholder={`delete ${currentWorkspace.name}`}
                  />
                </div>
                {deleteError && (
                  <div className="p-2 bg-red-100 border border-red-300 rounded text-sm text-red-700">
                    {deleteError}
                  </div>
                )}
                <Button
                  variant="destructive"
                  onClick={handleDeleteWorkspace}
                  disabled={
                    deleteConfirmation !== `delete ${currentWorkspace.name}` || deleteLoading
                  }
                >
                  {deleteLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting…
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Workspace
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
